import { COLORS } from "../../../presentation/constants/colors"
import React from "react"
import { useNavigate, useLocation } from "react-router-dom"

/**
 * Props for the SidebarTabButton component.
 */
export interface SidebarTabButtonProps {
  /**
   * Unique identifier for the button element.
   */
  id: string

  /**
   * The route path the button navigates to when clicked.
   */
  path: string

  /**
   * Label text displayed inside the button.
   */
  label: string

  /**
   * Icon component displayed inside the button.
   */
  icon: React.ReactNode

  /**
   * Optional click handler function.
   * If not provided, the button will navigate to the specified path.
   */
  onClick?: () => void

  /**
   * Optional array of paths that should be considered active.
   */
  matchPaths?: string[]
}

/**
 * SidebarTabButton component renders a button for sidebar navigation.
 * It highlights when the current route matches the given path.
 *
 * @param {SidebarTabButtonProps} props - The properties for the SidebarTabButton component.
 * @returns {JSX.Element} The rendered sidebar tab button.
 */
const SidebarTabButton: React.FC<SidebarTabButtonProps> = ({
  id,
  path,
  label,
  icon,
  onClick,
  matchPaths,
}) => {
  const navigate = useNavigate()
  const location = useLocation()

  // Determine if the current path matches the button's path or any additional match paths
  const isActive = (() => {
    const pathsToMatch = matchPaths || [path]
    return pathsToMatch.some((p) => {
      if (p.includes(":terminalId")) {
        return location.pathname.startsWith(p.split(":")[0])
      }
      return location.pathname === p
    })
  })()

  const handleClick = () => {
    if (onClick) {
      onClick()
    } else {
      navigate(path)
    }
  }

  return (
    <button
      data-automation-id={id}
      id={id}
      onClick={handleClick}
      className={`flex items-center gap-2 sm:gap-3 px-2 py-2 sm:py-4 w-full text-left transition-all duration-300 rounded-l-[20px] sm:rounded-l-[30px]
        ${
          isActive
            ? "bg-gray-200 font-semibold w-full highlighted-tab"
            : "text-white hover:bg-gray-600/20"
        }`}
        style={{
          color: isActive ? COLORS.SIDEBAR_ACTIVE_TEXT : undefined
        }}
      aria-label={label}
    >
      <span className="text-sm sm:text-base">{icon}</span>
      <span className="text-sm sm:text-base">{label}</span>
    </button>
  )
}

export default SidebarTabButton
